<template>
  <Teleport to="body">
    <div class="howto-backdrop vote-spin-backdrop" :class="{ 'vote-spin--table': table }" @click="close">
      <div class="vote-spin" role="dialog" aria-modal="true" aria-label="The vote">
        <span class="vote-spin__kind">The town is split</span>
        <h3 class="vote-spin__title">{{ landed ? `It's ${pickedLabel}` : 'Deciding…' }}</h3>
        <div class="vote-spin__bar">
          <span
            v-for="s in segments"
            :key="s.id"
            class="vote-spin__seg"
            :class="{ 'vote-spin__seg--won': landed && s.id === picked }"
            :style="{ flexGrow: s.n }"
          >
            <span class="vote-spin__label">{{ s.label }}</span>
            <span class="vote-spin__odds">{{ s.n }} {{ s.n === 1 ? 'vote' : 'votes' }} · {{ s.pct }}%</span>
          </span>
          <span class="vote-spin__marker" :style="{ left: `${pos * 100}%` }" aria-hidden="true" />
        </div>
        <p class="vote-spin__foot">{{ landed ? 'Tap to go on.' : 'One vote drawn at random: more votes, better odds.' }}</p>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { prefersReducedMotion } from '@/utils/eventTheatre';

// THE SPLIT VOTE, given its moment (Matt, playtest 9: "it should then do a
// dramatic little animation while it chooses"). The engine has already
// chosen; this only shows it. The marker swings across the bar - each
// choice as wide as its share of the votes - slows, and settles in the one
// that was drawn. A tap skips it; reduced motion skips straight to the end.
const SPIN_MS = 3200;

export default {
  name: 'VoteSpin',
  props: {
    // choice id -> votes
    tally: { type: Object, required: true },
    picked: { type: String, required: true },
    // choice id -> what the button said
    labels: { type: Object, default: () => ({}) },
    table: { type: Boolean, default: false },
  },
  emits: ['close'],
  data() {
    return {
      pos: 0, landed: false, raf: null, end: 0.5,
    };
  },
  computed: {
    total() { return Object.values(this.tally).reduce((n, v) => n + v, 0) || 1; },
    segments() {
      return Object.entries(this.tally).map(([id, n]) => ({
        id, n, label: this.labels[id] || id, pct: Math.round((n / this.total) * 100),
      }));
    },
    pickedLabel() { return this.labels[this.picked] || this.picked; },
  },
  mounted() {
    this.end = this.landingSpot();
    if (prefersReducedMotion()) { this.pos = this.end; this.landed = true; return; }
    const { end } = this;
    const start = performance.now();
    const step = (now) => {
      const t = Math.min(1, (now - start) / SPIN_MS);
      // Swings that shrink as it slows: three passes over the bar, then home.
      const swing = Math.sin(t * Math.PI * 6) * (1 - t) ** 2 * 0.5;
      this.pos = Math.max(0, Math.min(1, end + swing));
      if (t < 1) this.raf = requestAnimationFrame(step);
      else { this.pos = end; this.landed = true; }
    };
    this.raf = requestAnimationFrame(step);
  },
  beforeUnmount() { cancelAnimationFrame(this.raf); },
  methods: {
    // Somewhere inside the winning segment, not dead centre.
    landingSpot() {
      let from = 0;
      for (const s of this.segments) {
        const w = s.n / this.total;
        if (s.id === this.picked) return from + w * (0.3 + Math.random() * 0.4);
        from += w;
      }
      return 0.5;
    },
    close() {
      if (!this.landed) { cancelAnimationFrame(this.raf); this.pos = this.end; this.landed = true; return; }
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss" scoped>
.vote-spin {
  width: min(560px, 100%);
  background: #14181f; color: #eef1f5; border-radius: 14px; border: 2px solid #d9a441;
  padding: 18px; display: flex; flex-direction: column; gap: 12px;
}
.vote-spin--table .vote-spin { width: min(900px, 90vw); padding: 28px; }
.vote-spin__kind { font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.08em; color: #d9a441; font-weight: 800; }
.vote-spin__title { margin: 0; font-size: 1.5rem; }
.vote-spin--table .vote-spin__title { font-size: 2.4rem; }
.vote-spin__bar { position: relative; display: flex; gap: 3px; height: 92px; }
.vote-spin__seg {
  flex-basis: 0; min-width: 0; border-radius: 8px; background: #252c37;
  display: flex; flex-direction: column; justify-content: center; align-items: center; padding: 6px; text-align: center;
  transition: background 0.3s, transform 0.3s;
}
.vote-spin__seg--won { background: #d9a441; color: #111; transform: scale(1.03); }
.vote-spin__label { font-weight: 800; font-size: 0.95rem; line-height: 1.2; }
.vote-spin__odds { font-size: 0.78rem; opacity: 0.85; }
.vote-spin__marker {
  position: absolute; top: -10px; bottom: -10px; width: 4px; margin-left: -2px;
  background: #fff; border-radius: 2px; box-shadow: 0 0 10px rgba(255, 255, 255, 0.8);
}
.vote-spin__foot { margin: 0; font-size: 0.85rem; color: #9aa6b4; }
</style>
